<template>
  <v-container fluid v-if="processedFilterOptions.length">
    <v-row no-gutters>
      <v-col class="text-right">
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :outlined="!filterCount"
          @click="onFilterModalShown"
        >
          <v-icon>fas fa-filter</v-icon>
          <span v-if="filterCount">({{ filterCount }})</span>
        </v-btn>
      </v-col>
    </v-row>

    <BGModal :shown="filterModalShown" title="Filter">
      <template #content>
        <v-container>
          <v-row>
            <v-col
              cols="12"
              md="6"
              lg="4"
              v-for="option in processedFilterOptions"
            >
              <v-autocomplete
                :items="sort(option.options, 'text')"
                :label="option.text"
                outlined
                :chips="!option.single"
                :small-chips="!option.single"
                :deletable-chips="!option.single"
                :multiple="!option.single"
                clearable
                hide-details
                v-if="option.options"
                :value="tempDisplayValues[option.value]"
                @change="onComboboxInput(option, $event)"
              ></v-autocomplete>
              <DatePickerInput
                range
                hide-details
                :label="
                  option.text +
                  (option.date_range || option.datetime_date_range
                    ? ' (Range)'
                    : '')
                "
                v-if="option.date_range || option.datetime_date_range"
                :value="
                  tempDisplayValues[option.value]
                    ? tempDisplayValues[option.value]
                    : []
                "
                @input="onDatePickerInput(option, $event)"
              ></DatePickerInput>
              <v-text-field
                outlined
                hide-details
                :label="option.text"
                v-if="option.contains"
                :value="tempFilters[option.value]"
                @input="onTexFieldInput(option, $event)"
              ></v-text-field>
              <v-switch
                :label="option.text"
                :value="tempFilters[option.value] === option.boolean"
                v-if="option.hasOwnProperty('boolean')"
                @change="onCheckboxInput(option, $event)"
              ></v-switch>
            </v-col>
          </v-row>
        </v-container>
      </template>
      <template #footer>
        <v-row no-gutters>
          <v-btn
            color="secondary"
            @click="filterModalShown = false"
            class="mr-3"
            depressed
            large
          >
            {{ $t("cancel") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="secondary"
            @click="clearTempFilters"
            class="mr-3"
            depressed
            large
          >
            CLEAR
          </v-btn>
          <v-btn color="primary" depressed large @click="onFilterModalFinish">
            APPLY <span v-if="tempFilterCount">({{ tempFilterCount }})</span>
            <v-icon right>fas fa-check</v-icon>
          </v-btn>
        </v-row>
      </template>
    </BGModal>
  </v-container>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import _ from "lodash";
import DatePickerInput from "./DatePickerInput";
import { DateTime } from "luxon";
import BGModal from "./BGModal";

export default {
  name: "AdvancedSearchInput",
  components: { BGModal, DatePickerInput },
  props: {
    value: String,
    filterOptions: Array,
    excludeFilterFields: Array,
    defaultFilters: Object,
  },
  setup(props, { root, emit }) {
    const search = ref(props.value);
    watch(search, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        emit("input", newVal);
      }
    });

    const processedFilterOptions = computed(() => {
      let options = [];
      let excludeFilterFields = props.excludeFilterFields || [];

      if (props.filterOptions) {
        options = _.filter(
          props.filterOptions,
          (o) =>
            excludeFilterFields.indexOf(o.value) === -1 &&
            _.some(
              [
                "options",
                "date_range",
                "datetime_date_range",
                "contains",
                "boolean",
              ],
              (k) => o.hasOwnProperty(k)
            )
        );
      }

      return options;
    });
    const showFilterMenu = ref(null);
    const filters = ref({
      ...props.defaultFilters,
    });
    const filterCount = ref(0);
    const displayValues = ref({
      ...props.defaultFilters,
    });

    const filterModalShown = ref(false);
    const tempFilters = ref({
      ...props.defaultFilters,
    });
    const tempFilterCount = ref(0);
    const tempDisplayValues = ref({
      ...props.defaultFilters,
    });
    const onFilterModalShown = function () {
      tempFilters.value = { ...filters.value };
      tempFilterCount.value = Object.keys(tempFilters.value).length;
      tempDisplayValues.value = { ...displayValues.value };
      filterModalShown.value = true;
    };
    const onFilterModalFinish = function () {
      filters.value = { ...tempFilters.value };
      filterCount.value = Object.keys(filters.value).length;
      displayValues.value = { ...tempDisplayValues.value };
      filterModalShown.value = false;
      emit("filter", filters.value);
    };
    const onComboboxInput = function (option, value) {
      let existingDisplayValues = tempDisplayValues.value;
      let existingFilters = tempFilters.value;
      if (option.single ? value !== null : value.length) {
        // existingDisplayValues[option.value] = value.map(v => typeof v === "string" ? v : v.text);
        existingDisplayValues[option.value] = option.exact
          ? { exact: true, value }
          : value;
        // existingFilters[option.value] = value.map(v => typeof v === "string" ? v : v.value);
        existingFilters[option.value] = option.exact
          ? { exact: true, value }
          : value;
      } else {
        delete existingDisplayValues[option.value];
        delete existingFilters[option.value];
      }

      tempDisplayValues.value = existingDisplayValues;
      tempFilters.value = existingFilters;
      emit("temp-filter-changed", tempFilters.value);

      tempFilterCount.value = Object.keys(tempFilters.value).length;
    };
    const onDatePickerInput = function (option, value) {
      let existingDisplayValues = tempDisplayValues.value;
      let existingFilters = tempFilters.value;
      if (option.date_range && value.length === 2) {
        existingDisplayValues[option.value] = value;
        existingFilters[option.value] = { date_range: true, value: value };
      } else if (option.datetime_date_range && value.length === 2) {
        existingDisplayValues[option.value] = value;
        existingFilters[option.value] = {
          datetime_date_range: true,
          value: [
            DateTime.fromISO(value[0]).startOf("day").toISO(),
            DateTime.fromISO(value[1]).endOf("day").toISO(),
          ],
        };
      } else {
        delete existingDisplayValues[option.value];
        delete existingFilters[option.value];
      }

      tempDisplayValues.value = existingDisplayValues;
      tempFilters.value = existingFilters;
      emit("temp-filter-changed", tempFilters.value);

      tempFilterCount.value = Object.keys(tempFilters.value).length;
    };
    const onTexFieldInput = function (option, value) {
      let existingFilters = tempFilters.value;
      if (value.length) {
        existingFilters[option.value] = value;
      } else {
        delete existingFilters[option.value];
      }

      tempFilters.value = existingFilters;
      emit("temp-filter-changed", tempFilters.value);

      tempFilterCount.value = Object.keys(tempFilters.value).length;
    };
    const onCheckboxInput = function (option, value) {
      let existingFilters = tempFilters.value;
      if (value) {
        existingFilters[option.value] = option.boolean;
      } else {
        delete existingFilters[option.value];
      }

      tempFilters.value = existingFilters;
      emit("temp-filter-changed", tempFilters.value);

      tempFilterCount.value = Object.keys(tempFilters.value).length;
    };
    const clearTempFilters = function () {
      tempDisplayValues.value = {};
      tempFilters.value = {};
      tempFilterCount.value = 0;
    };

    const sort = function (collection, iteratees) {
      return _.sortBy(collection, iteratees);
    };

    watch(
      () => processedFilterOptions.value,
      (newValue) => {
        // Cleanup filter
        let currentFilters = { ...tempFilters.value };
        let currentDisplayValues = { ...tempDisplayValues.value };

        let cleanedDisplayValues = {};
        let cleanedFilters = {};

        for (const key in currentFilters) {
          const filterOption = _.find(props.filterOptions, { value: key });

          if (filterOption) {
            if (filterOption.options) {
              let filtered = _.filter(currentFilters[key], (v) =>
                _.find(filterOption.options, { value: v })
              );

              if (filtered.length > 0) {
                cleanedFilters[key] = filtered;
                cleanedDisplayValues[key] = filtered;
              }
            } else {
              cleanedFilters[key] = currentFilters[key];
              cleanedDisplayValues[key] = currentDisplayValues[key];
            }
          }
        }

        tempFilters.value = cleanedFilters;
        tempDisplayValues.value = cleanedDisplayValues;
      }
    );

    onMounted(() => {
      filterCount.value = Object.keys(filters.value).length;
    });

    // const applyFilters = function () {
    //   emit('filter', filters.value);
    //   showFilterMenu.value = false;
    // };
    // const clearFilters = function () {
    //   displayValues.value = {};
    //   filters.value = {};
    //   filterCount.value = 0;
    //   applyFilters();
    // };

    return {
      search,
      processedFilterOptions,
      showFilterMenu,
      filters,
      filterCount,
      displayValues,

      filterModalShown,
      tempFilters,
      tempFilterCount,
      tempDisplayValues,
      onFilterModalShown,
      onFilterModalFinish,
      onComboboxInput,
      onDatePickerInput,
      onTexFieldInput,
      onCheckboxInput,
      clearTempFilters,
      sort,
      // applyFilters,
      // clearFilters,
      _,
    };
  },
};
</script>

<style lang="less">
.advanced-search-input {
  .v-input__slot {
    padding-right: 0 !important;

    .v-input__append-inner {
      margin-top: 2px;
      margin-left: 0;
      margin-bottom: 0;
      /*padding-right: 53px;*/
    }
  }

  .advanced-search-filter-btn {
    /*padding: 0 !important;*/
  }

  .advanced-search-input-icon {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
}
</style>
